import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { menuItems } from '../estructura/Sidebar';

const BusquedaRapida = () => {
  const [termino, setTermino] = useState('');
  const [resultados, setResultados] = useState([]);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [indiceFoco, setIndiceFoco] = useState(-1);
  const navigate = useNavigate();
  const refContenedor = useRef(null);
  const refInput = useRef(null);

  // Aplanar menuItems (incluir submenus)
  const itemsAplanados = React.useMemo(() => {
    const items = [];
    menuItems.forEach((item) => {
      if (item.href && item.href !== '#') {
        items.push({ label: item.label, href: item.href, icon: item.icon, padre: null });
      }
      if (item.submenu) {
        item.submenu.forEach((sub) => {
          items.push({ label: sub.label, href: sub.href, icon: item.icon, padre: item.label });
        });
      }
    });
    return items;
  }, []);

  // Filtrar resultados al escribir
  useEffect(() => {
    if (termino.trim() === '') {
      setResultados([]);
      setMostrarResultados(false);
      return;
    }
    const filtrados = itemsAplanados.filter((item) =>
      item.label.toLowerCase().includes(termino.toLowerCase()) ||
      (item.padre && item.padre.toLowerCase().includes(termino.toLowerCase()))
    );
    setResultados(filtrados);
    setMostrarResultados(true);
    setIndiceFoco(-1);
  }, [termino, itemsAplanados]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickFuera = (e) => {
      if (refContenedor.current && !refContenedor.current.contains(e.target)) {
        setMostrarResultados(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  const handleSeleccionar = (href) => {
    navigate(href);
    setTermino('');
    setMostrarResultados(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndiceFoco((prev) => (prev < resultados.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndiceFoco((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' && indiceFoco >= 0 && resultados[indiceFoco]) {
      handleSeleccionar(resultados[indiceFoco].href);
    } else if (e.key === 'Escape') {
      setMostrarResultados(false);
      refInput.current?.blur();
    }
  };

  return (
    <div ref={refContenedor} className="relative w-full max-w-md">
      <div className="relative">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
        <input
          ref={refInput}
          type="text"
          placeholder="Buscar módulos, pantallas..."
          value={termino}
          onChange={(e) => setTermino(e.target.value)}
          onFocus={() => termino.trim() && setMostrarResultados(true)}
          onKeyDown={handleKeyDown}
          className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-300 rounded-xl outline-none focus:border-color-secundario focus:ring-2 focus:ring-color-secundario focus:ring-opacity-20 transition-all placeholder-gray-400"
        />
      </div>

      {/* Dropdown de resultados */}
      {mostrarResultados && resultados.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-64 overflow-y-auto">
          {resultados.map((item, index) => {
            const Icono = item.icon;
            return (
              <button
                key={item.href}
                onClick={() => handleSeleccionar(item.href)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-50 transition-colors ${
                  index === indiceFoco ? 'bg-gray-100' : ''
                } ${index === 0 ? 'rounded-t-xl' : ''} ${
                  index === resultados.length - 1 ? 'rounded-b-xl' : ''
                }`}
              >
                {Icono && <Icono className="text-color-primario text-base flex-shrink-0" />}
                <div className="flex flex-col">
                  <span className="text-gray-800">{item.label}</span>
                  {item.padre && (
                    <span className="text-xs text-gray-400">{item.padre}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Sin resultados */}
      {mostrarResultados && termino.trim() && resultados.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 px-4 py-3">
          <p className="text-sm text-gray-400 text-center">No se encontraron resultados</p>
        </div>
      )}
    </div>
  );
};

export default BusquedaRapida;
